import React, { useState } from 'react';
import {
  BookOpen,
  Calendar,
  Star,
  Trash2,
  Sparkles,
  Check,
  ChevronRight,
  Shirt,
  X,
  Download,
  Info,
} from 'lucide-react';
import { Outfit } from '../types';
import { api } from '../lib/api';

interface LookbookViewProps {
  outfits: Outfit[];
  isLoading: boolean;
  onOutfitUpdated: (updated: Outfit) => void;
  onOutfitDeleted: (id: string) => void;
  onSwitchToStudio: () => void;
}

export const LookbookView: React.FC<LookbookViewProps> = ({
  outfits,
  isLoading,
  onOutfitUpdated,
  onOutfitDeleted,
  onSwitchToStudio,
}) => {
  const [selectedOutfit, setSelectedOutfit] = useState<Outfit | null>(null);

  // Log wearing outfit today
  const handleLogWear = async (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await api.outfits.logWear(id);
      const outfit = outfits.find((o) => o.id === id);
      if (outfit) {
        const updated = {
          ...outfit,
          last_worn_at: new Date().toISOString(),
        };
        onOutfitUpdated(updated);
        if (selectedOutfit?.id === id) {
          setSelectedOutfit(updated);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Star rating
  const handleRate = async (id: string, rating: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await api.outfits.rate(id, rating);
      const outfit = outfits.find((o) => o.id === id);
      if (outfit) {
        const updated = {
          ...outfit,
          rating,
        };
        onOutfitUpdated(updated);
        if (selectedOutfit?.id === id) {
          setSelectedOutfit(updated);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Delete outfit
  const handleDelete = async (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this saved outfit from your lookbook?')) return;

    try {
      await api.outfits.delete(id);
      onOutfitDeleted(id);
      if (selectedOutfit?.id === id) {
        setSelectedOutfit(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Download try-on image
  const handleDownload = (imgUrl: string, name: string) => {
    const link = document.createElement('a');
    link.href = imgUrl;
    link.download = `${name.replace(/[^a-zA-Z0-9_-]/g, '_')}-tryon.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <span className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
              <BookOpen className="w-5 h-5" />
            </span>
            <h2 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight">
              My Lookbook &amp; Outfits
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Saved outfit combinations, photorealistic AI try-on shots, and rotation wear history.
          </p>
        </div>

        <button
          onClick={onSwitchToStudio}
          className="self-start sm:self-auto inline-flex items-center space-x-2 px-4 py-2 rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white text-xs font-bold shadow-md shadow-brand-600/30 transition-all hover:scale-105"
        >
          <Sparkles className="w-4 h-4" />
          <span>Create New Fit</span>
        </button>
      </div>

      {/* Outfits List / Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-slate-900 border border-slate-800 rounded-3xl h-64 animate-pulse" />
          ))}
        </div>
      ) : outfits.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/40 border border-dashed border-slate-800 rounded-3xl p-8 max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-2xl bg-brand-500/10 text-brand-400 flex items-center justify-center mx-auto mb-4 border border-brand-500/20">
            <BookOpen className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-white mb-1">No saved outfits yet</h3>
          <p className="text-xs sm:text-sm text-slate-400 mb-6">
            Head over to the AI Try-On Studio, pick your favorite top, bottoms, and sneakers, and save your fit!
          </p>
          <button
            onClick={onSwitchToStudio}
            className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-bold shadow-lg shadow-brand-600/30 transition-all"
          >
            Open Try-On Studio
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {outfits.map((outfit) => {
            let feedbackObj: any = null;
            if (outfit.ai_feedback) {
              try {
                feedbackObj = JSON.parse(outfit.ai_feedback);
              } catch (e) {
                // Ignore
              }
            }

            return (
              <div
                key={outfit.id}
                onClick={() => setSelectedOutfit(outfit)}
                className="group bg-slate-900/90 hover:bg-slate-850 border border-slate-800 hover:border-brand-500/40 rounded-3xl p-5 shadow-lg transition-all duration-300 flex flex-col justify-between cursor-pointer"
              >
                <div>
                  {/* Top row: Title & Delete */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-bold text-base text-white group-hover:text-brand-300 transition-colors">
                        {outfit.name}
                      </h4>
                      <div className="flex items-center space-x-2 mt-1">
                        {outfit.occasion && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                            {outfit.occasion}
                          </span>
                        )}
                        {outfit.season && (
                          <span className="text-[10px] text-slate-400">
                            {outfit.season}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={(e) => handleDelete(outfit.id, e)}
                      className="p-1.5 rounded-xl text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Delete outfit"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Primary Visual: AI Try-On Image OR Garment Pieces Grid */}
                  {outfit.tryon_image ? (
                    <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-slate-950 border border-slate-800 mb-3 group/img">
                      <img
                        src={outfit.tryon_image}
                        alt={outfit.name}
                        className="w-full h-full object-contain filter brightness-[1.01] group-hover/img:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-lg bg-slate-950/80 backdrop-blur-md text-brand-300 text-[10px] font-bold border border-brand-500/30 flex items-center gap-1 shadow-md">
                        <Sparkles className="w-3 h-3 text-brand-400" />
                        <span>AI Try-On</span>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 my-4 bg-slate-950 p-2.5 rounded-2xl border border-slate-800/80">
                      {outfit.top_image ? (
                        <div className="aspect-square rounded-xl bg-slate-900 overflow-hidden relative group/item">
                          <img
                            src={outfit.top_image}
                            alt="Top"
                            className="w-full h-full object-contain p-1"
                          />
                          {outfit.top_brand && (
                            <span className="absolute bottom-1 left-1 px-1.5 py-0.2 rounded bg-slate-950/80 text-slate-300 text-[9px] font-bold uppercase truncate max-w-[90%]">
                              {outfit.top_brand}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="aspect-square rounded-xl bg-slate-900/50 flex items-center justify-center text-slate-600 text-xs">
                          Top
                        </div>
                      )}

                      {outfit.bottom_image ? (
                        <div className="aspect-square rounded-xl bg-slate-900 overflow-hidden relative group/item">
                          <img
                            src={outfit.bottom_image}
                            alt="Bottom"
                            className="w-full h-full object-contain p-1"
                          />
                          {outfit.bottom_brand && (
                            <span className="absolute bottom-1 left-1 px-1.5 py-0.2 rounded bg-slate-950/80 text-slate-300 text-[9px] font-bold uppercase truncate max-w-[90%]">
                              {outfit.bottom_brand}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="aspect-square rounded-xl bg-slate-900/50 flex items-center justify-center text-slate-600 text-xs">
                          Bottom
                        </div>
                      )}

                      {outfit.shoes_image ? (
                        <div className="aspect-square rounded-xl bg-slate-900 overflow-hidden relative group/item">
                          <img
                            src={outfit.shoes_image}
                            alt="Shoes"
                            className="w-full h-full object-contain p-1"
                          />
                          {outfit.shoes_brand && (
                            <span className="absolute bottom-1 left-1 px-1.5 py-0.2 rounded bg-slate-950/80 text-slate-300 text-[9px] font-bold uppercase truncate max-w-[90%]">
                              {outfit.shoes_brand}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="aspect-square rounded-xl bg-slate-900/50 flex items-center justify-center text-slate-600 text-xs">
                          Shoes
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Stylist Score if available */}
                  {feedbackObj?.fit_score && (
                    <div className="bg-brand-500/10 border border-brand-500/20 rounded-xl p-2.5 flex items-center justify-between mb-3">
                      <span className="text-[11px] font-semibold text-brand-300 flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>AI Fit Score</span>
                      </span>
                      <span className="text-xs font-extrabold text-white">
                        {feedbackObj.fit_score} / 10
                      </span>
                    </div>
                  )}

                  {/* Star Rating */}
                  <div className="flex items-center space-x-1 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={(e) => handleRate(outfit.id, star, e)}
                        className="p-1 hover:scale-110 transition-transform"
                      >
                        <Star
                          className={`w-4 h-4 ${
                            star <= outfit.rating
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-slate-600'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bottom Card Footer */}
                <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between">
                  <span className="text-[11px] text-slate-400">
                    {outfit.last_worn_at
                      ? `Worn ${new Date(outfit.last_worn_at).toLocaleDateString()}`
                      : 'Not worn yet'}
                  </span>

                  <button
                    onClick={(e) => handleLogWear(outfit.id, e)}
                    className="px-3 py-1 rounded-xl bg-slate-800 hover:bg-brand-600 text-slate-200 hover:text-white text-xs font-semibold transition-colors flex items-center gap-1"
                  >
                    <Check className="w-3 h-3" />
                    <span>Wear Today</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Selected Outfit Detail Modal */}
      {selectedOutfit && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto"
          onClick={() => setSelectedOutfit(null)}
        >
          <div
            className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl my-auto max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/70">
              <div>
                <h3 className="text-lg font-bold text-white">{selectedOutfit.name}</h3>
                <div className="flex items-center space-x-2 mt-0.5">
                  {selectedOutfit.occasion && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-800 text-slate-300">
                      {selectedOutfit.occasion}
                    </span>
                  )}
                  {selectedOutfit.season && (
                    <span className="text-xs text-slate-400">
                      {selectedOutfit.season}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {selectedOutfit.tryon_image && (
                  <button
                    onClick={() => handleDownload(selectedOutfit.tryon_image!, selectedOutfit.name)}
                    className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors border border-slate-700"
                    title="Download try-on image"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setSelectedOutfit(null)}
                  className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Scrollable Body */}
            <div className="p-5 overflow-y-auto space-y-5 flex-1">
              {/* Try-on Image Banner */}
              {selectedOutfit.tryon_image && (
                <div className="relative aspect-[3/4] max-h-[440px] rounded-2xl overflow-hidden bg-slate-950 border border-slate-800 flex items-center justify-center mx-auto">
                  <img
                    src={selectedOutfit.tryon_image}
                    alt={selectedOutfit.name}
                    className="h-full w-auto object-contain"
                  />
                  <div className="absolute bottom-3 left-3 bg-slate-950/80 backdrop-blur-md border border-slate-700/80 px-2.5 py-1 rounded-xl text-xs text-white flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-brand-400" />
                    <span className="font-bold text-brand-200">AI Synthesized Fit</span>
                  </div>
                </div>
              )}

              {/* Pieces Breakdown */}
              <div className="space-y-2">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                  Garments in this Outfit:
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {selectedOutfit.top_name && (
                    <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center space-x-2">
                      {selectedOutfit.top_image && (
                        <img src={selectedOutfit.top_image} alt="" className="w-10 h-10 object-contain rounded-lg bg-slate-900" />
                      )}
                      <div className="truncate">
                        <span className="text-[10px] text-slate-400 font-bold uppercase block">Top</span>
                        <span className="text-xs font-semibold text-white truncate block">{selectedOutfit.top_name}</span>
                      </div>
                    </div>
                  )}
                  {selectedOutfit.bottom_name && (
                    <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center space-x-2">
                      {selectedOutfit.bottom_image && (
                        <img src={selectedOutfit.bottom_image} alt="" className="w-10 h-10 object-contain rounded-lg bg-slate-900" />
                      )}
                      <div className="truncate">
                        <span className="text-[10px] text-slate-400 font-bold uppercase block">Bottom</span>
                        <span className="text-xs font-semibold text-white truncate block">{selectedOutfit.bottom_name}</span>
                      </div>
                    </div>
                  )}
                  {selectedOutfit.outerwear_name && (
                    <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center space-x-2">
                      {selectedOutfit.outerwear_image && (
                        <img src={selectedOutfit.outerwear_image} alt="" className="w-10 h-10 object-contain rounded-lg bg-slate-900" />
                      )}
                      <div className="truncate">
                        <span className="text-[10px] text-slate-400 font-bold uppercase block">Outerwear</span>
                        <span className="text-xs font-semibold text-white truncate block">{selectedOutfit.outerwear_name}</span>
                      </div>
                    </div>
                  )}
                  {selectedOutfit.shoes_name && (
                    <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center space-x-2">
                      {selectedOutfit.shoes_image && (
                        <img src={selectedOutfit.shoes_image} alt="" className="w-10 h-10 object-contain rounded-lg bg-slate-900" />
                      )}
                      <div className="truncate">
                        <span className="text-[10px] text-slate-400 font-bold uppercase block">Footwear</span>
                        <span className="text-xs font-semibold text-white truncate block">{selectedOutfit.shoes_name}</span>
                      </div>
                    </div>
                  )}
                  {selectedOutfit.accessory_name && (
                    <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center space-x-2">
                      {selectedOutfit.accessory_image && (
                        <img src={selectedOutfit.accessory_image} alt="" className="w-10 h-10 object-contain rounded-lg bg-slate-900" />
                      )}
                      <div className="truncate">
                        <span className="text-[10px] text-slate-400 font-bold uppercase block">Accessory</span>
                        <span className="text-xs font-semibold text-white truncate block">{selectedOutfit.accessory_name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* AI Stylist Feedback */}
              {selectedOutfit.ai_feedback && (() => {
                let fb: any = null;
                try {
                  fb = JSON.parse(selectedOutfit.ai_feedback);
                } catch (e) {
                  return null;
                }
                if (!fb) return null;
                return (
                  <div className="p-4 rounded-2xl bg-slate-950 border border-brand-500/20 space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-2">
                      <span className="text-xs font-bold text-brand-300 uppercase tracking-wider flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4 text-brand-400" />
                        <span>Stylist Review: {fb.vibe_title || 'Look Verdict'}</span>
                      </span>
                      {fb.fit_score && (
                        <span className="text-sm font-extrabold text-white bg-brand-500/20 px-2.5 py-0.5 rounded-lg border border-brand-500/30">
                          {fb.fit_score} / 10
                        </span>
                      )}
                    </div>

                    {fb.color_harmony && (
                      <p className="text-xs text-slate-300">
                        <strong className="text-white block mb-0.5">Color Harmony:</strong> {fb.color_harmony}
                      </p>
                    )}
                    {fb.proportions_critique && (
                      <p className="text-xs text-slate-300">
                        <strong className="text-white block mb-0.5">Proportions &amp; Silhouette:</strong> {fb.proportions_critique}
                      </p>
                    )}
                    {fb.styling_tips && fb.styling_tips.length > 0 && (
                      <div>
                        <strong className="text-xs text-white block mb-1">Styling Advice:</strong>
                        <ul className="space-y-1 text-xs text-slate-300 list-disc list-inside">
                          {fb.styling_tips.map((t: string, idx: number) => (
                            <li key={idx}>{t}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer Actions */}
            <div className="p-4 border-t border-slate-800 bg-slate-950 flex items-center justify-between">
              <button
                onClick={() => handleDelete(selectedOutfit.id)}
                className="px-3 py-2 rounded-xl text-xs font-semibold text-rose-400 hover:bg-rose-500/10 transition-colors flex items-center gap-1.5"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>

              <button
                onClick={() => handleLogWear(selectedOutfit.id)}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white text-xs font-bold shadow-md shadow-brand-600/30 flex items-center gap-2 transition-all hover:scale-105"
              >
                <Check className="w-4 h-4" />
                <span>Mark Worn Today</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
